import React, {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type BaseTable from 'react-base-table';
import { WindowScroller } from 'react-virtualized/dist/commonjs/WindowScroller';
import config from 'config';

import { cn } from '@lyearn/style';
import { Box, useForkRef } from '@lyearn/ui';
import Loader from '@/components/Loader';
import dynamic from '@/utils/dynamic';

import { setTableHeightStyles, tableStyles } from './styles';
import { TableProps } from './types';

const LazyTable = dynamic(() => import(/* webpackChunkName: "react-base-table" */ './Table'));

const GET_SCROLLBAR_SIZE_FROZEN_RIGHT = () => 0.01;

const Table = <T extends object>(props: TableProps<T>, forwardedRef: React.Ref<BaseTable<T>>) => {
  const {
    className,
    useWindowScroll,
    onScroll,
    data,
    rowKey = 'id',
    columns,
    suspenseFallback = <Loader />,
    scrollElement,
    onRowClick,
    expandedRowKeys,
    ...rest
  } = props;
  const [baseTableHeight, setBaseTableHeight] = useState(0);
  const ref = useRef<BaseTable<T>>(null);
  const handleRef = useForkRef(forwardedRef, ref);
  const scrollTopRef = useRef(0);

  if (config.env === 'development') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      data?.forEach((datum) => {
        if (!(rowKey in datum)) {
          console.error({ datum });
          throw new Error(`Table: rowKey "${rowKey}" not found in data. Data is logged above.`);
        }
      });
    }, [data, rowKey]);
  }

  useEffect(() => {
    if (ref.current && useWindowScroll) {
      setBaseTableHeight(
        ref.current.getTotalRowsHeight() +
          (ref.current as TSFixMe)._getHeaderHeight() +
          (ref.current as TSFixMe)._horizontalScrollbarSize,
      );
    }
  }, [data, expandedRowKeys, useWindowScroll]);

  const handleWindowScroll = useCallback(
    ({ scrollTop }: { scrollLeft: number; scrollTop: number }) => {
      if (ref.current) {
        ref.current.scrollToTop(scrollTopRef.current);
        scrollTopRef.current = scrollTop;
      }
    },
    [ref],
  );

  const handleTableScroll = useCallback<NonNullable<TableProps['onScroll']>>(
    (args) => {
      onScroll?.(args);
      if (useWindowScroll && ref.current) {
        ref.current.scrollToTop(scrollTopRef.current);
      }
    },
    [onScroll, ref, useWindowScroll],
  );

  const rowEventHandlers = useMemo(
    () => (onRowClick ? { onClick: onRowClick } : undefined),
    [onRowClick],
  );

  const isFrozenRight = columns?.findIndex((column) => column.frozen === 'right') !== -1;

  return (
    <Suspense fallback={suspenseFallback}>
      {useWindowScroll ? (
        <WindowScroller scrollElement={scrollElement} onScroll={handleWindowScroll}>
          {() => <Box />}
        </WindowScroller>
      ) : null}
      <LazyTable
        className={cn(
          { [tableStyles]: useWindowScroll },
          { [setTableHeightStyles(baseTableHeight)]: baseTableHeight },
          className,
        )}
        columns={columns}
        data={data}
        expandedRowKeys={expandedRowKeys}
        getScrollbarSize={
          isFrozenRight && useWindowScroll ? GET_SCROLLBAR_SIZE_FROZEN_RIGHT : undefined
        }
        rowKey={rowKey}
        {...rest}
        ref={handleRef}
        rowEventHandlers={rowEventHandlers}
        onScroll={handleTableScroll}
      />
    </Suspense>
  );
};

LazyTable.preload();
export default forwardRef(Table) as typeof Table;
